import type { AwardNode } from 'data-layer';
import type { KeyVisualAwardOrientation } from '@pixum/key-visual/lib/types';
import { nth } from 'utils';
import { KEYVISUAL_TYPE } from '../../../constants';

/**
 * Determines if the award collection should be displayed.
 *
 * Award collections are only shown when:
 * 1. There are multiple awards (2 or more)
 * 2. AND the visual type is HIGH_CONTRAST_WITH_AWARDS
 *
 * @param awards - Array of award nodes
 * @param keyVisualType - The type of key visual being rendered (optional)
 * @returns True if award collection should be rendered
 *
 * @example
 * const showCollection = shouldRenderAwardCollection(awards, 'High-Contrast with Awards');
 * // Returns: true if awards.length > 1
 */
function shouldRenderAwardCollection(awards: (AwardNode | undefined)[], keyVisualType?: string): boolean {
  return Array.isArray(awards) && awards.length > 1 && keyVisualType === KEYVISUAL_TYPE.HIGH_CONTRAST_WITH_AWARDS;
}

/**
 * Gets the featured award to display within the key visual component.
 *
 * Returns the first award if we're NOT showing the award collection.
 * If showing the collection or no awards exist, returns null.
 *
 * @param awards - Array of award nodes
 * @param showAwardCollection - Whether the award collection is being rendered
 * @returns The featured award, null or undefined
 *
 * @example
 * const featured = getFeaturedAward(awards, false);
 * // Returns: First award from array
 */
function getFeaturedAward(
  awards: (AwardNode | undefined)[],
  showAwardCollection: boolean
): AwardNode | null | undefined {
  if (showAwardCollection || !awards?.length) {
    return null;
  }

  return nth(awards, 0, null);
}

/**
 * Determines the orientation of an award based on its image properties.
 *
 * @param award - The award node to check
 * @returns 'portrait' if the award image is portrait, otherwise 'landscape'
 *
 * @example
 * const orientation = getAwardOrientation(award);
 * // Returns: 'portrait' or 'landscape'
 */
function getAwardOrientation(award: AwardNode | null | undefined): KeyVisualAwardOrientation {
  return award?.image?.isPortrait ? 'portrait' : 'landscape';
}

export { shouldRenderAwardCollection, getFeaturedAward, getAwardOrientation };
